/**
 * Reine Parser für die Blog-Inhalte unter `content/blog/*.md`.
 *
 * Warum liegt der Artikeltext NICHT in `src/i18n/locales/**`?
 * Locale-Namespaces tragen UI-Bausteine (Labels, Buttons, Fehlermeldungen) und
 * unterliegen der Key-Parität DE↔EN — ein 2.000-Wörter-Fließtext dort erzwingt
 * eine englische Fassung im selben Commit und macht jeden Textfix zu einem
 * Eingriff in eine 98-KB-JSON. Redaktioneller Inhalt ist deshalb eine Datei je
 * Artikel unter `content/` (ausserhalb von `src/`, damit weder der
 * Deutsch-Prosa- noch der Markt-Ratchet darüber laufen). Die Bedien-Elemente
 * der Blog-Seiten (Nav, Buttons, Hinweise) bleiben normal in i18n.
 *
 * Alles hier ist frei von Netz, DB, Zeit und Zufall — die Glob-Anbindung sitzt
 * in `posts.ts`, damit dieser Teil unter Vitest testbar bleibt.
 */

export type BlogStatus = "draft" | "published";

export interface BlogFrontmatter {
  slug: string;
  seoTitle: string;
  seoDescription: string;
  teaser: string;
  published: string;
  modified: string;
  faqHeading: string;
  status: BlogStatus;
}

export interface BlogTocEntry {
  text: string;
  anchor: string;
}

export interface BlogFaqEntry {
  question: string;
  answer: string;
}

/** Ein Slot-Marker (`[[slot:tools-table]]`) trennt zwei Markdown-Blöcke. */
export type BlogBlock =
  | { type: "markdown"; markdown: string }
  | { type: "slot"; name: string };

export interface BlogArticle {
  frontmatter: BlogFrontmatter;
  /** H1 aus dem Markdown — die Seite rendert sie, nicht der Markdown-Renderer. */
  title: string;
  blocks: BlogBlock[];
  faq: BlogFaqEntry[];
  toc: BlogTocEntry[];
  readingMinutes: number;
  wordCount: number;
}

const SLOT_LINE = /^\[\[slot:([a-z0-9-]+)\]\]$/;
const H1_LINE = /^#\s+(.+?)\s*$/;
const H2_LINE = /^##\s+(.+?)\s*$/;
const FAQ_QUESTION_LINE = /^\*\*(.+?)\*\*$/;

/**
 * Code-Zäune und Code-Spannen für die Wortzählung.
 *
 * Über den Code-Punkt zusammengesetzt statt als Regex-Literal geschrieben: der
 * String-Extraktor von scripts/check-german-prose.mjs kennt keine
 * Regex-Literale und liest ein rohes Backtick darin als Beginn eines
 * Template-Strings. Bei ungerader Anzahl je Zeile läuft sein Zustand über den
 * Rest der Datei weiter und meldet danach deutsche Kommentare als Prosa.
 */
const TICK = String.fromCharCode(96);
const CODE_BLOCK = new RegExp(`${TICK}{3}[\\s\\S]*?${TICK}{3}`, "g");
const CODE_SPAN = new RegExp(`${TICK}[^${TICK}]*${TICK}`, "g");

/** Zeilen normalisieren: CRLF und BOM aus Windows-Editoren fallen raus. */
function toLines(text: string): string[] {
  return String(text ?? "").replace(/^﻿/, "").replace(/\r\n?/g, "\n").split("\n");
}

/**
 * Entfernt HTML-Kommentare (`<!-- … -->`, auch mehrzeilig).
 *
 * Redaktionelle TODO-Marker stehen als Kommentar direkt an der betroffenen
 * Stelle im Artikel. react-markdown rendert rohes HTML ohne `rehype-raw` zwar
 * ohnehin nicht, aber „wird schon nicht angezeigt" ist bei Text, der intern
 * gedachte Hinweise trägt, die falsche Zusicherung — deshalb fliegen sie hier
 * hart raus, bevor irgendetwas sie zu sehen bekommt.
 */
export function stripHtmlComments(markdown: string): string {
  return String(markdown ?? "").replace(/<!--[\s\S]*?-->/g, "");
}

/**
 * Trennt YAML-artiges Frontmatter vom Rumpf.
 *
 * Bewusst ein Mini-Parser statt `gray-matter`: gebraucht werden flache
 * `key: value`-Paare, optional in doppelten Anführungszeichen. Fehlt der
 * Frontmatter-Block, ist die ganze Datei Rumpf.
 */
export function parseFrontmatter(raw: string): {
  data: Record<string, string>;
  body: string;
} {
  const lines = toLines(raw);
  if (lines[0]?.trim() !== "---") {
    return { data: {}, body: lines.join("\n") };
  }

  const closing = lines.findIndex((line, index) => index > 0 && line.trim() === "---");
  if (closing < 0) {
    return { data: {}, body: lines.join("\n") };
  }

  const data: Record<string, string> = {};
  for (const line of lines.slice(1, closing)) {
    const separator = line.indexOf(":");
    if (separator <= 0) continue;
    const key = line.slice(0, separator).trim();
    if (!key || key.startsWith("#")) continue;
    let value = line.slice(separator + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"') && value.length >= 2)
      || (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
    ) {
      value = value.slice(1, -1);
    }
    data[key] = value;
  }

  return { data, body: lines.slice(closing + 1).join("\n").replace(/^\n+/, "") };
}

/**
 * Zieht die H1 aus dem Rumpf und gibt sie zurück.
 *
 * Die Seite rendert die Überschrift selbst, damit sie im Editorial-Stil des
 * Public-Auftritts steht. Bliebe sie zusätzlich im Markdown, hätte die Seite
 * zwei H1 — `scripts/check-public-seo.mjs` lässt genau eine zu.
 */
export function extractTitle(body: string): { title: string; body: string } {
  const lines = toLines(body);
  const index = lines.findIndex((line) => H1_LINE.test(line));
  if (index < 0) return { title: "", body: lines.join("\n") };

  const title = lines[index].match(H1_LINE)?.[1] ?? "";
  lines.splice(index, 1);
  return { title, body: lines.join("\n").replace(/^\n+/, "") };
}

/**
 * Anker aus einer Überschrift — spiegelt `github-slugger` (die Regel, nach der
 * `rehype-slug` die `id`-Attribute vergibt): klein, Satzzeichen raus, aber
 * Unicode-Buchstaben bleiben. Eine ASCII-Variante würde „Häufige" zu „hufige"
 * verkürzen, während die gerenderte Überschrift „häufige-fragen" trägt — jeder
 * Sprunglink im Inhaltsverzeichnis liefe dann ins Leere.
 */
export function slugifyHeading(text: string): string {
  return String(text ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N} -]/gu, "")
    .replace(/ /g, "-");
}

/** H2-Überschriften des Rumpfs → Inhaltsverzeichnis. */
export function extractToc(body: string): BlogTocEntry[] {
  const entries: BlogTocEntry[] = [];
  const seen = new Map<string, number>();
  for (const line of toLines(body)) {
    const match = line.match(H2_LINE);
    if (!match) continue;
    const text = match[1].replace(/\*\*/g, "").trim();
    const base = slugifyHeading(text);
    // Doppelte Überschriften bekommen bei rehype-slug ein Zählsuffix.
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    entries.push({ text, anchor: count === 0 ? base : `${base}-${count}` });
  }
  return entries;
}

/**
 * Schneidet den FAQ-Abschnitt heraus und zerlegt ihn in Frage/Antwort-Paare.
 *
 * Erwartet das Muster aus dem Artikel: die Frage als fette Zeile, die Antwort
 * in den Zeilen darunter. Als eigener Block gerendert bekommt jede Antwort ein
 * eigenes `<p>` statt mit der Frage in einem Absatz zu kleben — und dieselben
 * Paare speisen das FAQPage-Schema.
 */
export function extractFaq(
  body: string,
  heading: string,
): { body: string; faq: BlogFaqEntry[]; headingText: string } {
  const wanted = String(heading ?? "").trim().toLowerCase();
  const lines = toLines(body);
  const start = wanted
    ? lines.findIndex((line) => line.match(H2_LINE)?.[1].trim().toLowerCase() === wanted)
    : -1;
  if (start < 0) return { body: lines.join("\n"), faq: [], headingText: "" };

  const rest = lines.slice(start + 1);
  const endOffset = rest.findIndex((line) => H2_LINE.test(line));
  const section = endOffset < 0 ? rest : rest.slice(0, endOffset);
  const tail = endOffset < 0 ? [] : rest.slice(endOffset);

  const faq: BlogFaqEntry[] = [];
  let current: BlogFaqEntry | null = null;
  for (const line of section) {
    const question = line.trim().match(FAQ_QUESTION_LINE);
    if (question) {
      current = { question: question[1].trim(), answer: "" };
      faq.push(current);
      continue;
    }
    if (!current) continue;
    const text = line.trim();
    if (!text) continue;
    current.answer = current.answer ? `${current.answer} ${text}` : text;
  }

  return {
    body: [...lines.slice(0, start), ...tail].join("\n").replace(/\n{3,}/g, "\n\n"),
    faq: faq.filter((entry) => entry.question && entry.answer),
    headingText: lines[start].match(H2_LINE)?.[1].trim() ?? "",
  };
}

/**
 * Zerlegt den Rumpf an den Slot-Markern. Leere Markdown-Blöcke fallen weg,
 * damit die Seite keine Abstände für Nichts rendert.
 */
export function splitSlots(body: string): BlogBlock[] {
  const blocks: BlogBlock[] = [];
  let buffer: string[] = [];

  const flush = () => {
    const markdown = buffer.join("\n").trim();
    if (markdown) blocks.push({ type: "markdown", markdown });
    buffer = [];
  };

  for (const line of toLines(body)) {
    const slot = line.trim().match(SLOT_LINE);
    if (slot) {
      flush();
      blocks.push({ type: "slot", name: slot[1] });
      continue;
    }
    buffer.push(line);
  }
  flush();

  return blocks;
}

/**
 * Wörter des Fließtexts (Markdown-Auszeichnung zählt nicht mit).
 *
 * Code-Zaeune stehen als Unicode-Escape statt als Zeichen: der String-Extraktor
 * von scripts/check-german-prose.mjs kennt keine Regex-Literale und liest ein
 * rohes Backtick darin als Beginn eines Template-Strings. Bei ungerader Anzahl
 * je Zeile läuft sein Zustand über den Rest der Datei weiter und meldet
 * anschliessend Kommentare als deutsche Prosa-Strings.
 */
export function countWords(markdown: string): number {
  const plain = String(markdown ?? "")
    .replace(CODE_BLOCK, " ")
    .replace(CODE_SPAN, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_>|-]/g, " ")
    .trim();
  if (!plain) return 0;
  return plain.split(/\s+/).filter(Boolean).length;
}

/** Lesedauer in vollen Minuten, mindestens 1. 200 Wörter/Minute. */
export function readingMinutes(wordCount: number): number {
  return Math.max(1, Math.round((Number(wordCount) || 0) / 200));
}

/**
 * Datum eines Artikels in Langform („27. August 2026").
 *
 * Der Locale-Tag kommt vom Aufrufer (aus `PUBLIC_MARKETS`), damit hier kein
 * Sprach-Land-Tag festgeschrieben wird. Ein unlesbares Datum gibt den Rohwert
 * zurück, statt „Invalid Date" auf eine öffentliche Seite zu schreiben.
 */
export function formatArticleDate(iso: string, locale: string): string {
  const value = String(iso ?? "").trim();
  if (!value) return "";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function normalizeStatus(value: string | undefined): BlogStatus {
  return String(value ?? "").trim().toLowerCase() === "published" ? "published" : "draft";
}

/**
 * Datei-Inhalt → fertiger Artikel.
 *
 * `fallbackSlug` ist der Dateiname ohne Endung. Er gewinnt nur, wenn das
 * Frontmatter keinen `slug` trägt — die Datei ist die Adresse, und eine Datei
 * ohne Frontmatter soll trotzdem erreichbar sein.
 */
export function parseBlogArticle(raw: string, fallbackSlug: string): BlogArticle {
  const { data, body: frontmatterBody } = parseFrontmatter(raw);
  const cleaned = stripHtmlComments(frontmatterBody);
  const { title, body: titleless } = extractTitle(cleaned);

  const faqHeading = data.faq_heading ?? "";
  const { body: withoutFaq, faq, headingText } = extractFaq(titleless, faqHeading);

  const toc = extractToc(titleless);
  const wordCount = countWords(titleless);

  return {
    frontmatter: {
      slug: data.slug || fallbackSlug,
      seoTitle: data.seo_title || title,
      seoDescription: data.seo_description || "",
      teaser: data.teaser || "",
      published: data.published || "",
      modified: data.modified || data.published || "",
      faqHeading: headingText || faqHeading,
      status: normalizeStatus(data.status),
    },
    title,
    blocks: splitSlots(withoutFaq),
    faq,
    toc,
    wordCount,
    readingMinutes: readingMinutes(wordCount),
  };
}
